import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowRight, ArrowLeft, Stethoscope, Microscope, Pill, AlertCircle,
} from "lucide-react";
import api, { API } from "@/lib/api";

const photoSrc = (p) => (p && p.startsWith("http") ? p : `${API}/public/image/${p}`);

export default function SpecialtyDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [spec, setSpec] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/public/specialties/${id}`).then((r) => {
      const s = r.data.specialty;
      setSpec(s);
      api.get(`/public/doctors`, { params: { specialty: s.name } })
        .then((d) => setDoctors(d.data.doctors || []));
    }).catch(() => setNotFound(true));
  }, [id]);

  if (notFound)
    return <section className="pgwrap"><p className="pg-lead">Specialty not found. <Link to="/specialties">Back to specialties</Link></p></section>;
  if (!spec)
    return <section className="pgwrap"><p className="pg-lead">Loading…</p></section>;

  const photos = [...(spec.photos || [])].slice(0, 4);
  while (photos.length < 4) photos.push("");

  const lists = [
    { icon: AlertCircle, title: "When to seek this specialty", hint: "Consider a second opinion from this specialty if you have:", items: spec.symptoms || [] },
    { icon: Microscope, title: "Common investigations", hint: "Reports and tests our specialists review:", items: spec.investigations || [] },
    { icon: Pill, title: "Treatments offered", hint: "Approaches our specialists can advise on:", items: spec.treatments || [] },
  ];

  return (
    <section className="pgwrap" data-testid="specialty-detail">
      <button className="sp-back" onClick={() => nav("/specialties")} data-testid="sp-back">
        <ArrowLeft size={15} /> All specialties
      </button>

      <div className="sp-hero">
        <div className="sp-hero-main">
          <span className="pg-eyebrow">Specialty</span>
          <h2 className="pg-h2" data-testid="sp-name">{spec.name}</h2>
          {spec.about && <p className="pg-lead">{spec.about}</p>}
          {(spec.diseases || []).length > 0 && (
            <div className="sp-chips">{spec.diseases.map((d, i) => <span key={i}>{d}</span>)}</div>
          )}
          <button className="pg-btn pg-btn-green pg-btn-lg" style={{ marginTop: 20 }}
            onClick={() => nav(`/book?specialty=${encodeURIComponent(spec.name)}`)} data-testid="sp-book">
            Book consultation <ArrowRight size={16} />
          </button>
        </div>
        <div className="sp-collage" data-testid="sp-collage">
          {photos.map((p, i) => (
            <div className="tile" key={i}>
              {p && <img src={photoSrc(p)} alt={`${spec.name} ${i + 1}`} loading="lazy"
                onError={(e) => { e.target.style.display = "none"; }} />}
              <span className="ph"><Stethoscope size={26} /></span>
            </div>
          ))}
        </div>
      </div>

      <div className="sp-sections">
        {lists.filter((l) => l.items.length > 0).map((sec, i) => (
          <div className="sp-card" key={i} data-testid={`sp-section-${i}`}>
            <div className="sp-card-h"><sec.icon size={18} /><h3>{sec.title}</h3></div>
            <p className="sp-hint">{sec.hint}</p>
            <ul className="sp-list">{sec.items.map((x, j) => <li key={j}>{x}</li>)}</ul>
          </div>
        ))}
      </div>

      <div className="sp-doctors">
        <h3 className="pg-h3">Our doctors</h3>
        <p className="pg-lead" style={{ marginTop: 4 }}>Verified {spec.name} specialists available for a second opinion.</p>
        {doctors.length === 0 ? (
          <p className="sp-hint" style={{ marginTop: 14 }}>No doctors listed under this specialty yet — please check back soon.</p>
        ) : (
          <div className="sp-doctors-grid" data-testid="sp-doctors-grid">
            {doctors.map((d) => (
              <div className="doc-card" key={d.id} onClick={() => nav(`/doctor/${d.id}`)} data-testid={`sp-doctor-${d.id}`}>
                <div className="doc-ava">
                  {d.photo_id ? <img src={`${API}/public/image/${d.photo_id}`} alt={d.name} />
                    : <span>{d.name.replace("Dr. ", "").charAt(0)}</span>}
                </div>
                <div className="doc-meta">
                  <h4>{d.name}</h4>
                  <p>{d.specialty}{d.reg_no ? ` · ${d.reg_no}` : ""}</p>
                  {d.qualifications && <p className="doc-qual">{d.qualifications}</p>}
                </div>
                <span className="doc-arrow"><ArrowRight size={16} /></span>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
